import {
  ChallengeGrid,
  Header,
  HeaderContainer,
  RightHeaderContainer,
  SortByLabel,
  LeftContainer,
  RightContainer,
  ContentContainer,
  CustomTestHeading,
  HeadingContainer,
  DescriptionHeading,
  RightHeadingContainer,
  Link,
} from './styles';
import React, { useEffect, useState, useCallback } from 'react';

import { colors } from 'global/colors';
import { Wrapper, Container, Button } from '../../components';
import { userRoles } from 'util/appConstants';
import { useActions } from 'hooks/useActions';
import { Images } from 'util/images';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import CurvedContainer from '../../components/CurvedContainer';
import ChallengeCard from '../../components/ChallengeCard';
import SearchBar from '../../components/SearchBar';
import Sorting from '../../components/Sorting';
import Filter from '../../components/FilterSidebar';
import * as actions from './actions';

const searchQueryFilter = (searchQuery) => (object) => {
  return Object.values(object).some(
    (field) => field && String(field).toLowerCase().indexOf(searchQuery.toLowerCase()) > -1
  );
};

const tagFilter = (selectedTags) => (object) => {
  if (selectedTags.length > 0) {
    let noMatch = true;
    for (let i = 0; i < selectedTags.length; i++) {
      if (!object.keywords.find((t) => t === selectedTags[i])) {
        noMatch = false;
        break;
      }
    }
    return noMatch;
  }

  return true;
};

const ratingFilter = (selectedRating) => (object) => selectedRating ? object.averageRating >= selectedRating : true;

export default ({ match }) => {
  const { type } = match.params;
  const { getAllChallenges, setTagFilters } = useActions(actions);
  const { loading, error, challenges, selectedTagFilters } = useSelector((state) => state.challengeList);
  const { userRole, isCommunityUser } = useSelector((state) => state.auth);

  // On mount
  useEffect(() => {
    getAllChallenges(type);
  }, [getAllChallenges, type]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const heroImage = type === 'challenges' ? Images.elevator : Images.tanks;

  const [filterOptions, setFilterOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState(challenges.slice());
  const [selectedRating, setSelectedRating] = useState(3);
  const [hasFilters, setHasFilters] = useState(false);
  const [sortBy, setSortBy] = useState({
    value: 'averageRating',
    descendingOrder: true,
  });
  const [sortByOptions, setSortByOptions] = useState([
    { value: 'name', label: 'Name' },
    { value: 'createdAt', label: 'Newest' },
    { value: 'averageRating', label: 'Rating' },
    { value: 'totalCompletions', label: 'Participants' },
  ]);

  useEffect(() => {
    if (type === 'courses') {
      setSortByOptions([
        { value: 'name', label: 'Name' },
        { value: 'createdAt', label: 'Newest' },
      ]);
    } else {
      setSortByOptions([
        { value: 'name', label: 'Name' },
        { value: 'createdAt', label: 'Newest' },
        { value: 'averageRating', label: 'Rating' },
        { value: 'totalCompletions', label: 'Participants' },
      ]);
    }
  }, [type]);

  // Filters array when searchQuery, selectedTagFilters, selectedRating or itemsArray changes
  useEffect(() => {
    let filteredItems = challenges.filter(searchQueryFilter(searchQuery)).filter(tagFilter(selectedTagFilters));
    if (type !== 'courses') filteredItems = filteredItems.filter(ratingFilter(selectedRating));
    setItems(filteredItems);
    setHasFilters(selectedTagFilters.length > 0 || !!selectedRating);
  }, [searchQuery, selectedTagFilters, selectedRating, challenges]);

  const sortFunc = useCallback(
    (a, b) => {
      let desc = 1;
      let asc = -1;
      if (typeof a[sortBy.value] === 'number') {
        return sortBy.descendingOrder ? b[sortBy.value] - a[sortBy.value] : a[sortBy.value] - b[sortBy.value];
      }
      if (String(a[sortBy.value]).toLowerCase() > String(b[sortBy.value]).toLowerCase()) {
        return sortBy.descendingOrder
          ? sortBy.value === 'createdAt'
            ? asc
            : desc
          : sortBy.value === 'createdAt'
          ? desc
          : asc;
      }
      if (String(a[sortBy.value]).toLowerCase() < String(b[sortBy.value]).toLowerCase()) {
        return sortBy.descendingOrder
          ? sortBy.value === 'createdAt'
            ? desc
            : asc
          : sortBy.value === 'createdAt'
          ? asc
          : desc;
      }
      return 0;
    },
    [sortBy]
  );

  useEffect(() => {
    if (!challenges) return;
    const difficultyTags = ['Easy', 'Medium', 'Hard'];
    let hasDifficultyTags = false;
    let tagItems = new Set();

    for (const challenge of challenges) {
      for (const tag of challenge.keywords) {
        if (difficultyTags.includes(tag)) {
          hasDifficultyTags = true;
        } else {
          tagItems.add(tag);
        }
      }
    }

    tagItems = Array.from(tagItems).sort();

    let newOptions = [];

    if (tagItems.length) {
      newOptions.push({
        title: 'Tag',
        items: tagItems,
        type: 'keywords',
        selectedKeywords: selectedTagFilters,
        onChange: (selection) => {
          setTagFilters(selection);
        },
      });
    }
    if (hasDifficultyTags) {
      newOptions.push({
        title: 'Difficulty',
        items: difficultyTags,
        type: 'keywords',
        selectedKeywords: selectedTagFilters,
        onChange: (selection) => {
          setTagFilters(selection);
        },
      });
    }

    if (type !== 'courses')
      newOptions.push({
        title: 'Rating',
        type: 'rating',
        selectedRating,
        onChange: (selection) => {
          setSelectedRating(selection);
        },
      });
    setFilterOptions(newOptions);
  }, [challenges, selectedTagFilters, selectedRating]);

  const clearFilters = () => {
    setTagFilters([]);
    setSelectedRating(undefined);
  };

  const rootContainer = (
    <Container column>
      <HeadingContainer>
        <Header>
          {type === 'challenges'
            ? 'Challenges'
            : type === 'freecode'
            ? 'Freecode'
            : type === 'courses'
            ? 'Courses'
            : 'Exercises'}
        </Header>
        <RightHeadingContainer>
          <DescriptionHeading>
            {type === 'challenges'
              ? 'Compete against the community to ascend the leaderboard'
              : type === 'freecode'
              ? 'Simulations and other fun ways to use our platform'
              : type === 'courses'
              ? 'Learn the coding by solving exercises'
              : 'Practice your coding skills by solving exercises'}
          </DescriptionHeading>
          <CustomTestHeading>
            {type !== 'courses' && userRole === userRoles.CANDIDATE && isCommunityUser && (
              <Link to="/customTests">
                <Button
                  primaryBlue
                  primaryGreenBorder
                  noVerticalMargin
                  title={
                    'Create your own ' +
                    (type === 'challenges' ? 'challenge' : type === 'freecode' ? 'freecode' : 'exercise')
                  }
                >
                  <i className="fa fa-plus">&nbsp;</i>Create
                </Button>
              </Link>
            )}
          </CustomTestHeading>
        </RightHeadingContainer>
      </HeadingContainer>
      <ContentContainer>
        <LeftContainer>
          <Filter items={items} hasFilters={hasFilters} clearFilters={clearFilters} options={filterOptions} />
        </LeftContainer>
        <RightContainer>
          <HeaderContainer>
            <SearchBar value={searchQuery} onChange={setSearchQuery} />
            <RightHeaderContainer>
              <SortByLabel>Sort by:</SortByLabel>
              <Sorting
                value={sortBy.value}
                descendingOrder={sortBy.descendingOrder}
                options={sortByOptions}
                onChange={(value) => setSortBy(value)}
              />
            </RightHeaderContainer>
          </HeaderContainer>
          {!loading ? (
            <ChallengeGrid isExercises={type === 'exercises' || type === 'freecode'}>
              {items.sort(sortFunc).map((item) => (
                <ChallengeCard {...item} link={`/${type}/${item._id}`} type={type} key={item._id} />
              ))}
            </ChallengeGrid>
          ) : (
            <div>Loading...</div>
          )}
        </RightContainer>
      </ContentContainer>
    </Container>
  );

  return (
    <Wrapper color="transparent">
      <CurvedContainer bottom height={110} color={colors.darkBlue} image={heroImage}></CurvedContainer>
      {rootContainer}
    </Wrapper>
  );
};
